<template>
  <transactions-table
    :is-billsRegistries-loading="isBillsRegistriesLoading"
    class="bills-registries-table"
    grid-template-columns="82px 200px 1fr 1.1fr 1.3fr 60px"
  >
    <template #tableHeader>
      <transactions-table-header
        class="transactions-table__header"
        :header-cols="$options.HEADER_COLS"
        @sortChanged="handleSortChanged"
      ></transactions-table-header>
    </template>
    <template #transactionItems>
      <bills-registry-table-item
        :bills-registry="registry"
        v-for="registry in billsRegistriesPaged"
        :key="registry.id"
        @deleted="handleRegistryDeleted"
        @click="handleBillsRegistryItemClicked"
      >
      </bills-registry-table-item>
    </template>
    <template #tablePagination>
      <transactions-table-pagination
        @changePage="handlePageChanged"
        :current-page="currentPage"
        :items-count="billsRegistriesFiltered.length"
        :per-page="perPage"
      ></transactions-table-pagination>
    </template>
  </transactions-table>
</template>

<script>
import { $on, $off, $once, $emit } from '../../../../utils/gogocodeTransfer'
import TransactionsTable from '@vue-app/components/company/transactions/table/TransactionsTable.vue'
import TransactionsTableHeader from '@vue-app/components/company/transactions/table/TransactionsTableHeader.vue'
import TransactionsTableItem from '@vue-app/components/company/transactions/table/TransactionsTableItem.vue'
import TransactionsTablePagination from '@vue-app/components/company/transactions/table/TransactionsTablePagination.vue'
import PayoutsToCardTableItem from '@vue-app/components/company/payouts/to-card/table/item/PayoutsToCardTableItem.vue'
import BillsRegistryTableItem from '@vue-app/components/company/bills/by-registry/bills-registry-table-item.vue'
import PayoutsRegistryTableItem from '@vue-app/components/company/payouts/by-registry/table/item/PayoutsRegistryTableItem.vue'
import BillsRegistry from '@js-app/entities/payouts/BillsRegistry'
import PayoutsRegistry from '@js-app/entities/payouts/PayoutsRegistry'

import BillsTableItem from '@vue-app/components/company/bills/table/item/BillsTableItem.vue'
import { mapState } from 'vuex'
import moment from 'moment/moment'

export default {
  name: 'BillsRegistriesTable',
  components: {
    BillsRegistryTableItem,
    PayoutsRegistryTableItem,
    PayoutsToCardTableItem,
    TransactionsTablePagination,
    TransactionsTableItem,
    TransactionsTableHeader,
    TransactionsTable,
    BillsTableItem,
  },
  props: {
    billsRegistriesList: {
      type: Array,
      default() {
        return []
      },
    },
    isBillsRegistriesLoading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    currentPage: 1,
    perPage: 10,
    sort: {
      col: null,
      dir: null,
    },
  }),
  HEADER_COLS: [
    {
      id: 'date',
      hasSort: true,
      sortKey: 'date',
      title: 'payouts.by-registry.table.column.date',
      additionalClasses: ['transactions-table-header__col--date'],
    },
    {
      id: 'name',
      hasSort: true,
      sortKey: 'name',
      title: 'payouts.by-registry.table.column.name',
      additionalClasses: ['transactions-table-header__col--name'],
    },
    {
      id: 'payoutsCount',
      hasSort: true,
      sortKey: 'payoutsCount',
      title: 'payouts.by-registry.table.column.payouts-count',
      additionalClasses: ['transactions-table-header__col--p-count'],
    },
    {
      id: 'payoutsSum',
      hasSort: true,
      sortKey: 'payoutsSum',
      title: 'payouts.by-registry.table.column.payouts-sum',
      additionalClasses: ['transactions-table-header__col--p-sum'],
    },
    {
      id: 'status',
      hasSort: true,
      sortKey: 'status',
      title: 'payouts.by-registry.table.column.status',
      additionalClasses: ['transactions-table-header__col--status'],
    },
    {
      id: 'action',
      hasSort: false,
      title: 'payouts.by-registry.table.column.action',
      additionalClasses: ['transactions-table-header__col--action'],
    },
  ],
  watch: {
    billsRegistriesFiltered() {
      this.currentPage = 1
    },
  },
  computed: {
    ...mapState(['selectedCompany']),
    billsRegistriesPaged() {
      let _startFrom = (this.currentPage - 1) * this.perPage
      return this.billsRegistriesFiltered.slice(
        _startFrom,
        _startFrom + this.perPage
      )
    },
    billsRegistriesFiltered() {
      return [...this.billsRegistriesSorted]
    },
    billsRegistriesSorted() {
      if (!this.sort || !this.sort.col) {
        return this.billsRegistries
      }

      return [...this.billsRegistries].sort(
        (fBillsRegistry, sBillsRegistry) => {
          let _firstGrater = false

          switch (this.sort.col) {
            case 'date': {
              let _firstDate = moment(fBillsRegistry.datePlain)
              let _secondDate = moment(sBillsRegistry.datePlain)

              if (_firstDate > _secondDate) {
                _firstGrater = true
              }
              break
            }
            case 'name': {
              if (fBillsRegistry.registryName > sBillsRegistry.registryName) {
                _firstGrater = true
              }

              break
            }
            case 'payoutsCount': {
              if (fBillsRegistry.totalCount > sBillsRegistry.totalCount) {
                _firstGrater = true
              }

              break
            }
            case 'payoutsSum': {
              if (
                parseInt(fBillsRegistry.totalSumPlain) >
                parseInt(sBillsRegistry.totalSumPlain)
              ) {
                _firstGrater = true
              }

              break
            }
            case 'status': {
              if (
                parseInt(fBillsRegistry.status) >
                parseInt(sBillsRegistry.status)
              ) {
                _firstGrater = true
              }

              break
            }
          }

          switch (this.sort.dir) {
            case 'DESC': {
              if (_firstGrater) {
                return -1
              } else {
                return 1
              }
            }
            case 'ASC': {
              if (_firstGrater) {
                return 1
              } else {
                return -1
              }
            }
          }
        }
      )
    },
    billsRegistries() {
      return this.billsRegistriesList.map(
        (_transObject) => new BillsRegistry(_transObject)
      )
    },
  },
  methods: {
    handleBillsRegistryItemClicked(billsRegistryId) {
      this.$router.push({
        name: 'bills-registry',
        params: {
          id: this.selectedCompany,
          billsRegistryId,
        },
      })
    },
    handleRegistryDeleted(billsRegistry) {
      $emit(this, 'registryDeleted', billsRegistry)
    },
    handlePageChanged(page) {
      this.currentPage = page
    },
    handleSortChanged(newSort) {
      this.sort = { ...newSort }
    },
  },
  emits: ['registryDeleted'],
}
</script>

<style lang="scss" scoped>
.bills-registries-table {
  max-width: 835px;
  :deep(.transactions-table) {
    .transactions-table__header {
      padding: 30px 10px;
      background: #ffffff;
      border-radius: 20px 20px 0 0;
      border-bottom: 1px solid #eff2f8;
    }

    .transactions-table__body {
      background: #ffffff;
      border-radius: 0 0 20px 20px;
      padding-bottom: 30px;
    }
  }

  :deep(.transactions-table-header) {
    .transactions-table-header__col {
      .transactions-table-header__col--name {
        justify-content: flex-start;
      }

      .transactions-table-header__col--p-sum,
      .transactions-table-header__col--p-count {
        justify-content: flex-end;
      }

      .transactions-table-header__col--status {
        justify-content: center;
      }

      .transactions-table-header__col--action {
        justify-content: center;
      }
    }
  }
}
</style>
